import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setShowBuilder } from 'actions/search';
import { Form, Button, Icon, SearchButton } from 'components/common';
import { ActionButtons, SearchAreaContainer } from './styled';
import { getShowBuilder } from 'selectors/search';
import SearchBuilder from '../../Search/SearchArea/SearchBuilder';
import OrgFilterField from '../Filters/OrgFilterField';
import { setOrgClinicalTrialFilterGroups } from 'actions/organisation';
import { SearchBar } from '.';
import {
  getHasValidClinicalTrialSearchQuery,
  getOrganisationClinicalTrialFilter,
  getOrganisationLoadingClinicalTrialData,
  SLICE,
} from 'selectors/organisation';
import { performBoolSearch, performPhraseSearch } from 'selectors/userSettings';
import BoolErrorMessage from 'components/Search/BoolErrorMessage';
import { SearchDiv } from 'components/Item/styled';

export default function OrganisationClinicalTrialSearchArea({
  organisation,
  handleSearch,
  handleCancel,
  loading,
}) {
  const dispatch = useDispatch();
  const showBuilder = useSelector(getShowBuilder);

  const handleClear = () => {
    dispatch(setOrgClinicalTrialFilterGroups(organisation.id, 'term', ''));
    handleSearch();
  };

  const searchTermRef = useRef();

  const [showBoolError, setShowBoolError] = useState(false);
  const getHasBoolError = useSelector(
    performBoolSearch(SLICE, organisation.id, 'clinicalTrialFilters')
  );
  const getHasPhraseError = useSelector(
    performPhraseSearch(SLICE, organisation.id, 'clinicalTrialFilters')
  );

  const handleSearchBuilderClear = () => {
    dispatch(setOrgClinicalTrialFilterGroups(organisation.id, 'term', ''));
  };

  const handleSearchBuilderSearch = value => {
    dispatch(setOrgClinicalTrialFilterGroups(organisation.id, 'term', value));
    handleSearch();
  };

  const handleShowBuilder = () => {
    dispatch(setShowBuilder(true));
  };

  const hasValidQuery = useSelector(
    getHasValidClinicalTrialSearchQuery(organisation.id)
  );

  const handleLocalSearch = () => {
    if (showBoolError !== null && (getHasBoolError || getHasPhraseError))
      setShowBoolError(true);
    else handleSearch();
  };

  const isDataLoading = useSelector(
    getOrganisationLoadingClinicalTrialData(organisation.id)
  );

  const isLoading = loading || isDataLoading;

  return (
    organisation && (
      <SearchAreaContainer style={{ marginBottom: 10 }}>
        <SearchBar>
          <Button
            style={{ marginRight: 10 }}
            type='secondary'
            onClick={handleShowBuilder}
            disabled={isLoading}
          >
            <Icon icon='magnifyingGlassPlus' color='#8D8D8D' size='sm' />
            <span style={{ marginLeft: '5px' }}>Open Search Builder</span>
          </Button>
          <SearchDiv>
            <Form width='100%' onSubmit={handleLocalSearch} style={{ flex: 1 }}>
              <OrgFilterField
                placeholder='(NGS OR "Next generation sequencing") AND CRISPR'
                filterId='term'
                organisationId={organisation.id}
                filterType='input'
                data-testid='org-filter-term'
                size='default'
                setFilterGroup={setOrgClinicalTrialFilterGroups}
                getFilter={getOrganisationClinicalTrialFilter}
                disabled={isLoading}
              />
            </Form>
            {showBoolError ? (
              <BoolErrorMessage
                performSearch={handleSearch}
                hasBoolFailed={getHasBoolError}
                hasParseFailed={getHasPhraseError}
                updateSearchQuery={value => handleSearchBuilderSearch(value)}
                term={getOrganisationClinicalTrialFilter(
                  organisation.id,
                  'term'
                )}
                searchTermRef={searchTermRef}
                setShowBoolError={setShowBoolError}
              />
            ) : (
              <></>
            )}
          </SearchDiv>
          <ActionButtons>
            <Button
              type='secondary'
              onClick={handleClear}
              data-testid='clear-search-button'
              disabled={isLoading}
            >
              Clear
            </Button>
            <SearchButton
              type='primary'
              data-testid='search-button'
              loading={isLoading}
              disabled={!hasValidQuery || isLoading}
              onSearch={handleLocalSearch}
              onCancel={handleCancel}
              location='OrganisationClinicalTrialsSearch'
            />
          </ActionButtons>
        </SearchBar>
        {!!showBuilder && (
          <SearchBuilder
            onSearch={handleSearchBuilderSearch}
            showAdvancedFields={false}
            id={organisation.id}
            saveInStore={false}
            onClear={handleSearchBuilderClear}
            filterId='term'
            filterGroup='OrganisationClinicalTrials'
          />
        )}
      </SearchAreaContainer>
    )
  );
}
